// @vitest-environment happy-dom
/**
 * TrialStatusBanner — always-visible trial/license bar for the desktop shell.
 *
 * Shows ONLY on desktop, ONLY when the account does NOT have a lifetime license.
 *
 *  - Active trial  → blue/brand bar with days remaining + Activate + Buy.
 *  - No license    → amber bar with "No active license" copy + Activate + Buy.
 *  - Licensed      → null (renders nothing; zero cost).
 *
 * Not dismissible — owner's requirement.
 *
 * Activate → dispatches `scrivar:open-activation` (App.jsx listens and calls
 *             setShowActivation(true), mirroring the CommandPalette open-event
 *             pattern from AppHeader.jsx).
 * Buy      → openExternalUrl(SCRIVAR_BILLING_URL) in the system browser.
 */
import { AlertTriangle, Key, ShoppingCart } from 'lucide-react';
import { useBilling } from '@/hooks/useBilling';
import { getTrialDaysRemaining } from '@/lib/license';
import { openExternalUrl, SCRIVAR_BILLING_URL } from '@/lib/external';

export default function TrialStatusBanner() {
  const { hasLifetime } = useBilling({ autoRefresh: false });

  // Licensed owners see nothing.
  if (hasLifetime) return null;

  const daysLeft = getTrialDaysRemaining();
  const activeTrial = typeof daysLeft === 'number' && daysLeft > 0;

  function handleActivate() {
    document.dispatchEvent(new CustomEvent('scrivar:open-activation'));
  }

  function handleBuy() {
    openExternalUrl(SCRIVAR_BILLING_URL);
  }

  if (activeTrial) {
    return (
      <div
        aria-live="polite"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '7px 20px',
          background: 'var(--ds-indigo-50)',
          borderBottom: '1px solid var(--ds-indigo-100)',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: '13px', color: 'var(--ds-indigo-strong)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Free trial — {daysLeft} day{daysLeft !== 1 ? 's' : ''} left · full Pro tools
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <button
            onClick={handleActivate}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '3px 10px', borderRadius: '6px', fontSize: '12px',
              fontWeight: 600, cursor: 'pointer',
              background: 'var(--ds-indigo)', color: 'var(--ds-content, #fff)', border: 'none',
            }}
          >
            <Key size={12} />
            Activate license
          </button>
          <button
            onClick={handleBuy}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '3px 10px', borderRadius: '6px', fontSize: '12px',
              fontWeight: 600, cursor: 'pointer',
              background: 'var(--ds-content)', color: 'var(--ds-indigo)',
              border: '1px solid var(--ds-indigo)',
            }}
          >
            <ShoppingCart size={12} />
            Buy <span style={{ opacity: 0.65 }}>Lite $49 · Pro $70</span>
          </button>
        </div>
      </div>
    );
  }

  // No license / trial ended.
  return (
    <div
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '7px 20px',
        background: 'var(--ds-amber-bg)',
        borderBottom: '1px solid var(--ds-amber-line, #FDE08D)',
        flexShrink: 0,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: 'var(--ds-amber)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <AlertTriangle size={14} style={{ flexShrink: 0 }} />
        No active license — start a trial or get a license to keep every tool
      </span>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <button
          onClick={handleActivate}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '3px 10px', borderRadius: '6px', fontSize: '12px',
            fontWeight: 600, cursor: 'pointer',
            background: 'var(--ds-amber)', color: 'var(--ds-content, #fff)', border: 'none',
          }}
        >
          <Key size={12} />
          Activate license
        </button>
        <button
          onClick={handleBuy}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '3px 10px', borderRadius: '6px', fontSize: '12px',
            fontWeight: 600, cursor: 'pointer',
            background: 'var(--ds-content)', color: 'var(--ds-amber)',
            border: '1px solid var(--ds-amber-line, #FDE08D)',
          }}
        >
          <ShoppingCart size={12} />
          Buy <span style={{ opacity: 0.65 }}>Lite $49 · Pro $70</span>
        </button>
      </div>
    </div>
  );
}
